import { Address } from '../../../../types/generic';
import AddressAutocomplete from '../../../../shared/AddressAutocomplete';
import FormTextField from '../../../../shared/FormTextField';
import {
    Box,
    Collapse,
    Grid,
    InputLabel,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CheckCircle, ExpandMore } from '@mui/icons-material';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useGetIndividualContractorQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import ProfileReadOnlyField, {
    ProfileShellFormCell,
    ProfileShellInlineSectionSubtitle,
    ProfileShellInlineSectionTitle,
} from '../../ProfileReadOnlyField';

const AddressDetails = ({
    isEditing,
    compactShell = false,
    companyShell = false,
}: {
    isEditing: boolean;
    compactShell?: boolean;
    companyShell?: boolean;
}) => {
    const theme = useTheme();
    const loggedInUser = UserRole();
    const { control, setValue, clearErrors } = useFormContext();

    const streetAddress = useWatch({ control, name: 'streetAddress' }) as
        | string
        | undefined;
    const suburb = useWatch({ control, name: 'suburb' }) as string | undefined;
    const postcode = useWatch({ control, name: 'postcode' }) as
        | string
        | undefined;

    const [manualExpanded, setManualExpanded] = useState(false);

    const { data: contractor } = useGetIndividualContractorQuery(
        loggedInUser?.company,
        { skip: !loggedInUser?.company },
    );

    const simplifiedAddress = `${contractor?.streetAddress}, ${contractor?.suburb} ${contractor?.postcode}, ${contractor?.state}, ${contractor?.countryCode}`;

    const handleAddress = (address: Address) => {
        clearErrors([
            'streetAddress',
            'suburb',
            'state',
            'postcode',
            'countryCode',
        ]);
        setValue('streetAddress', address.streetAddress, { shouldDirty: true });
        setValue('suburb', address.suburb, { shouldDirty: true });
        setValue(
            'state',
            address.state as
                | 'NSW'
                | 'QLD'
                | 'TAS'
                | 'VIC'
                | 'ACT'
                | 'WA'
                | 'SA'
                | 'NT',
            { shouldDirty: true },
        );
        setValue('postcode', address.postcode, { shouldDirty: true });
        setValue('countryCode', address.country as 'AU', { shouldDirty: true });
    };

    const accent = theme.palette.custom.sapphire;
    const addressLooksFilled = Boolean(
        streetAddress?.trim() && suburb?.trim() && postcode?.trim(),
    );

    if (compactShell || companyShell) {
        const shellKind = companyShell ? 'company' : 'compact';

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    border: 'none',
                    boxShadow: 'none',
                    bgcolor: 'transparent',
                    p: 0,
                    ...(shellKind === 'compact'
                        ? {
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              minHeight: 0,
                          }
                        : {}),
                }}
            >
                <Stack
                    spacing={2}
                    sx={
                        shellKind === 'compact'
                            ? { flex: 1, minHeight: 0 }
                            : undefined
                    }
                >
                    {shellKind === 'compact' ? (
                        <Box>
                            <ProfileShellInlineSectionTitle>
                                3. Service location
                            </ProfileShellInlineSectionTitle>
                            <ProfileShellInlineSectionSubtitle>
                                Add your business address. We&apos;ll use this
                                to show your location to clients.
                            </ProfileShellInlineSectionSubtitle>
                        </Box>
                    ) : null}

                    {!isEditing ? (
                        <ProfileReadOnlyField label='Address'>
                            {simplifiedAddress}
                        </ProfileReadOnlyField>
                    ) : (
                        <>
                            <ProfileShellFormCell
                                label={
                                    shellKind === 'company'
                                        ? 'Address'
                                        : 'Search address'
                                }
                                labelAdornment={
                                    <Box
                                        component='span'
                                        sx={{
                                            color: 'error.main',
                                            fontWeight: 600,
                                        }}
                                        aria-hidden
                                    >
                                        *
                                    </Box>
                                }
                            >
                                <AddressAutocomplete
                                    onChange={handleAddress}
                                    placeholder={
                                        shellKind === 'company'
                                            ? 'Search or enter manually.'
                                            : 'Search address'
                                    }
                                    fullWidth
                                />
                            </ProfileShellFormCell>

                            {addressLooksFilled ? (
                                <Stack
                                    direction='row'
                                    spacing={1}
                                    alignItems='center'
                                    sx={{ color: accent }}
                                >
                                    <CheckCircle sx={{ fontSize: 20 }} />
                                    <Typography
                                        variant='body2'
                                        fontWeight={600}
                                    >
                                        Address found. Please confirm the
                                        details below.
                                    </Typography>
                                </Stack>
                            ) : null}

                            {shellKind === 'compact' ? (
                                <Box
                                    component='button'
                                    type='button'
                                    onClick={() => setManualExpanded((v) => !v)}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        border: 'none',
                                        background: 'none',
                                        cursor: 'pointer',
                                        p: 0,
                                        font: 'inherit',
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        textAlign: 'left',
                                        typography: 'body2',
                                    }}
                                >
                                    Edit address manually
                                    <ExpandMore
                                        sx={{
                                            fontSize: 22,
                                            transition:
                                                theme.transitions.create(
                                                    'transform',
                                                ),
                                            transform: manualExpanded
                                                ? 'rotate(180deg)'
                                                : 'rotate(0deg)',
                                        }}
                                    />
                                </Box>
                            ) : null}

                            <Collapse
                                in={
                                    shellKind === 'company'
                                        ? true
                                        : manualExpanded
                                }
                            >
                                <Grid
                                    container
                                    columnSpacing='50px'
                                    rowSpacing='10px'
                                >
                                    <Grid container item xs={12} lg={6}>
                                        <Grid item xs={12} lg={5}>
                                            <InputLabel required>
                                                Street Address
                                            </InputLabel>
                                        </Grid>
                                        <Grid item xs={12} lg={7}>
                                            <FormTextField
                                                name='streetAddress'
                                                control={control}
                                                placeholder='230 Oak Road'
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12} lg={6}>
                                        <Grid item xs={12} lg={5}>
                                            <InputLabel required>
                                                Suburb
                                            </InputLabel>
                                        </Grid>
                                        <Grid item xs={12} lg={7}>
                                            <FormTextField
                                                name='suburb'
                                                control={control}
                                                placeholder='Matcham'
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12} lg={6}>
                                        <Grid item xs={12} lg={5}>
                                            <InputLabel required>
                                                Post Code
                                            </InputLabel>
                                        </Grid>
                                        <Grid item xs={12} lg={7}>
                                            <FormTextField
                                                name='postcode'
                                                control={control}
                                                placeholder='2250'
                                                type='number'
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12} lg={6}>
                                        <Grid item xs={12} lg={5}>
                                            <InputLabel required>
                                                State
                                            </InputLabel>
                                        </Grid>
                                        <Grid item xs={12} lg={7}>
                                            <FormAutocomplete
                                                name='state'
                                                control={control}
                                                options={[
                                                    {
                                                        value: 'NSW',
                                                        label: 'NSW',
                                                    },
                                                    {
                                                        value: 'QLD',
                                                        label: 'QLD',
                                                    },
                                                    {
                                                        value: 'VIC',
                                                        label: 'VIC',
                                                    },
                                                    {
                                                        value: 'SA',
                                                        label: 'SA',
                                                    },
                                                    {
                                                        value: 'WA',
                                                        label: 'WA',
                                                    },
                                                    {
                                                        value: 'NT',
                                                        label: 'NT',
                                                    },
                                                    {
                                                        value: 'TAS',
                                                        label: 'TAS',
                                                    },
                                                    {
                                                        value: 'ACT',
                                                        label: 'ACT',
                                                    },
                                                ]}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12} lg={6}>
                                        <Grid item xs={12} lg={5}>
                                            <InputLabel required>
                                                Country
                                            </InputLabel>
                                        </Grid>
                                        <Grid item xs={12} lg={7}>
                                            <FormAutocomplete
                                                name='countryCode'
                                                control={control}
                                                options={[
                                                    {
                                                        value: 'AU',
                                                        label: 'Australia',
                                                    },
                                                ]}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </>
                    )}
                </Stack>
            </Paper>
        );
    }

    return (
        <>
            <Typography
                sx={{ fontWeight: 'bold', color: 'text.primary', py: '30px' }}
            >
                Address
            </Typography>
            <Grid container columnSpacing='50px' rowSpacing='10px'>
                {!isEditing ? (
                    <Grid item xs={12}>
                        <ProfileReadOnlyField label='Address'>
                            {simplifiedAddress}
                        </ProfileReadOnlyField>
                    </Grid>
                ) : (
                    <Grid item container xs={12}>
                        <Grid item xs={12} lg={2.43}>
                            <InputLabel>Address</InputLabel>
                        </Grid>
                        <Grid item xs>
                            <AddressAutocomplete onChange={handleAddress} />
                        </Grid>
                    </Grid>
                )}
                {isEditing && (
                    <>
                        <Grid container item xs={12} lg={6}>
                            <Grid item xs={12} lg={5}>
                                <InputLabel required>Street Address</InputLabel>
                            </Grid>
                            <Grid item xs={12} lg={7}>
                                <FormTextField
                                    name='streetAddress'
                                    control={control}
                                    placeholder='230 Oak Road'
                                />
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} lg={6}>
                            <Grid item xs={12} lg={5}>
                                <InputLabel required>Suburb</InputLabel>
                            </Grid>
                            <Grid item xs={12} lg={7}>
                                <FormTextField
                                    name='suburb'
                                    control={control}
                                    placeholder='Matcham'
                                />
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} lg={6}>
                            <Grid item xs={12} lg={5}>
                                <InputLabel required>Post Code</InputLabel>
                            </Grid>
                            <Grid item xs={12} lg={7}>
                                <FormTextField
                                    name='postcode'
                                    control={control}
                                    placeholder='2250'
                                    type='number'
                                />
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} lg={6}>
                            <Grid item xs={12} lg={5}>
                                <InputLabel required>State</InputLabel>
                            </Grid>
                            <Grid item xs={12} lg={7}>
                                <FormAutocomplete
                                    name='state'
                                    control={control}
                                    options={[
                                        { value: 'NSW', label: 'NSW' },
                                        { value: 'QLD', label: 'QLD' },
                                        { value: 'VIC', label: 'VIC' },
                                        { value: 'SA', label: 'SA' },
                                        { value: 'WA', label: 'WA' },
                                        { value: 'NT', label: 'NT' },
                                        { value: 'TAS', label: 'TAS' },
                                        { value: 'ACT', label: 'ACT' },
                                    ]}
                                />
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} lg={6}>
                            <Grid item xs={12} lg={5}>
                                <InputLabel required>Country</InputLabel>
                            </Grid>
                            <Grid item xs={12} lg={7}>
                                <FormAutocomplete
                                    name='countryCode'
                                    control={control}
                                    options={[
                                        { value: 'AU', label: 'Australia' },
                                    ]}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </Grid>
        </>
    );
};

export default AddressDetails;
